// Reddit Ads pixel. The browser half of Reddit conversion tracking; the server
// half is backend/reddit_capi.py (Conversions API). A conversion both halves
// see (SignUp, Purchase) carries the same `conversionId` on each, so Reddit
// counts it once.
//
// Live only on the production domain and never inside the native app (no ATT
// prompt there), so dev, CI, previews and the WebView stay silent.

import { isNativeHost } from '@sweetspot/native-bridge';

export const REDDIT_PIXEL_ID = 'a2_jnv1a8o09jbc';

/** First-party cookie holding the ad click id (`?rdt_cid=`) for the backend's
 *  Conversions API calls. Read by backend/reddit_capi.py — keep in sync. */
export const REDDIT_CLICK_COOKIE = 'rt_rdt_cid';
const CLICK_COOKIE_MAX_AGE_S = 28 * 24 * 3600;

const PRODUCTION_HOST = /(^|\.)sweetspot\.diy$/;

export type RedditEvent = 'PageVisit' | 'SignUp' | 'AddToCart' | 'Purchase';

export interface RedditEventMeta {
  conversionId?: string;
  value?: number;
  currency?: string;
}

type Rdt = ((...args: unknown[]) => void) & { callQueue?: unknown[][]; sendEvent?: (...args: unknown[]) => void };
declare global {
  interface Window {
    rdt?: Rdt;
  }
}

/** The window the pixel was started on; null until it is live. */
let host: Window | null = null;

export function shouldLoadRedditPixel(win: Window): boolean {
  return PRODUCTION_HOST.test(win.location.hostname) && !isNativeHost(win);
}

function rememberClickId(win: Window): void {
  const clickId = new URLSearchParams(win.location.search).get('rdt_cid');
  if (!clickId) return;
  win.document.cookie = `${REDDIT_CLICK_COOKIE}=${encodeURIComponent(clickId)}; Max-Age=${CLICK_COOKIE_MAX_AGE_S}; Path=/; SameSite=Lax`;
}

/** Load the pixel and record the landing PageVisit. Idempotent. */
export function initRedditPixel(win: Window = window): void {
  if (host || !shouldLoadRedditPixel(win)) return;
  host = win;
  rememberClickId(win);
  // Reddit's loader, unminified: queue calls until pixel.js replaces the stub.
  let rdt = win.rdt;
  if (!rdt) {
    const stub: Rdt = (...args: unknown[]) => {
      if (stub.sendEvent) stub.sendEvent(...args);
      else stub.callQueue!.push(args);
    };
    stub.callQueue = [];
    win.rdt = rdt = stub;
    const script = win.document.createElement('script');
    script.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${REDDIT_PIXEL_ID}`;
    script.async = true;
    win.document.head.appendChild(script);
  }
  rdt('init', REDDIT_PIXEL_ID);
  rdt('track', 'PageVisit');
}

/** Send a conversion event. No-op until the pixel is live. */
export function redditTrack(event: RedditEvent, meta?: RedditEventMeta): void {
  host?.rdt?.('track', event, meta ?? {});
}

/** Test seam: forget which window the pixel started on. */
export function resetRedditPixelForTests(): void {
  host = null;
}
